/* Modbox — how the lock works.
   The animated cutaway from labs/lock-animation-design-lab (round three), ported as-is: the drawing,
   timelines, mail and key code below the "engine" line are the lab's own, unchanged. What's new is the
   wrapper at the bottom, which lets several drawings run on one page, each with its own settings.

   Mount:   <div data-mb-lock='{"product":"courier","text":true,"badge":true}'></div>
            options: product · text · badge · t (hold one frame) · range [from,to,once] · set {lab settings}
   Drive:   el.mbLock.post({ set:{…} } | { freeze: t|null } | { range: [from,to,once] })   (the lab's embed messages)
            el.mbLock.step(i)   one walkthrough step: plays it, or shows its still frame with reduced motion
   Reduced motion: no movement. Each beat is a still frame; tap the drawing or its progress marks to step. */
(function () {
  if (window.MBLock) return;

  // the approved picks (lab defaults, 2026-10-12)
  const DEFAULTS = { optional: 'on', concept: 'beats', clear: 35, letter: 'bubble', key: 'twist', aero: 'crisp', copy: 'roles',
    hold: 1.5, mailIn: 'tumble', mailOut: 'lift', gap: '2', finish: 'teal' };
  let selections = Object.assign({}, DEFAULTS);

  /* ════════════════════════ engine (from the lab, unchanged) ════════════════════════ */
const FINISH = {
  teal:     { main: '#21898b', dark: '#156264', light: '#43b0b0' },
  slate:    { main: '#5f7d90', dark: '#435a69', light: '#86a2b4' },
  orange:   { main: '#e2772c', dark: '#ad5516', light: '#f6a15f' },
  charcoal: { main: '#3c3e40', dark: '#232527', light: '#62666a' },
};
const gapLabel = () => ({ '0.5': '½″', '1': '1″', '1.5': '1½″', '2': '2″' })[selections.gap];

/* ════════════ geometry ════════════
   Flat side cutaway — an explainer diagram, not a spec drawing. S = px per inch so the opening reads to scale. */
const n2 = v => Math.round(v * 10) / 10;
function hex(h) { if (h[0] !== '#') return h.match(/\d+/g).map(Number); const v = parseInt(h.slice(1), 16); return [v >> 16, (v >> 8) & 255, v & 255]; }
function mix(a, b, t) { const A = hex(a), B = hex(b); return '#' + A.map((x, i) => Math.round(x + (B[i] - x) * t).toString(16).padStart(2, '0')).join(''); }
const clamp = (x, a = 0, b = 1) => Math.min(b, Math.max(a, x));
const deg = d => d * Math.PI / 180;
function door(Hx, Hy, Tx, Ty) { return { H: { x: Hx, y: Hy }, L: Math.hypot(Tx - Hx, Ty - Hy), base: Math.atan2(Tx - Hx, Hy - Ty) }; }
const GEOM = {
  courier: Object.assign(door(198, 362, 217, 84), {
    S: 22, open: deg(38), wing: 64, handle: .7, C: { x: 176, y: 112 }, dimY: 30, inset: { x: 322, y: 116, r: 46 },
    vb: '56 -30 350 404', scene: 'wall', env: { w: 6.5, h: 3.6 },
    rest: { x: 140, y: 357, psi: deg(-8) }, out: { d1: [75, -8], d2: [175, -60], d3: [275, -175], psi: deg(20) },
    side: 'M80,52 H190 Q212,52 212,76 L193,362 L86,362 Q80,362 80,356 Z',
  }),
};
const AERO = {
  crisp: { d: [437, 280, 458, 108], C: { x: 412, y: 140 },
    side: 'M40,150 L448,100 Q452,99.5 452,104 L432,280 L48,280 Q40,280 40,272 Z',
    hood: 'M40,150 L448,100 Q452,99.5 452,104 L441,200 L40,226 Z' },
  soft: { d: [437, 280, 458, 133], C: { x: 408, y: 150 },
    side: 'M40,150 L420,106 Q452,102.3 452,130 L432,280 L48,280 Q40,280 40,272 Z',
    hood: 'M40,150 L420,106 Q452,102.3 452,130 L443,200 L40,226 Z' },
  nose: { d: [437, 280, 457, 155], C: { x: 402, y: 152 },
    side: 'M40,150 L396,111 C430,107 452,120 452,152 L432,280 L48,280 Q40,280 40,272 Z',
    hood: 'M40,150 L396,111 C430,107 452,120 452,152 L444,200 L40,226 Z' },
};
function aeroGeom(v) {
  const A = AERO[v];
  return Object.assign(door(...A.d), {
    S: 18, open: deg(50), wing: 72, handle: .72, C: A.C, dimY: A.d[3] - 24, inset: { x: 190, y: 64, r: 40 },
    vb: '24 14 572 430', scene: 'yard', env: { w: 6.5, h: 3.6 }, side: A.side, hood: A.hood,
    rest: { x: 252, y: 276, psi: deg(90) }, out: { d1: [150, 0], d2: [270, -4], d3: [360, -50], psi: deg(70) },
  });
}
GEOM.aerobox = aeroGeom(selections.aero);
let GAP_NOW = null;   // set per frame while a timeline is stepping through latch settings
const lockAngle = G => ((GAP_NOW ?? parseFloat(selections.gap)) * G.S) / G.L;
const lockFor = (G, g) => g * G.S / G.L;
const fmtGap = g => [[.5, '½″'], [1, '1″'], [1.5, '1½″'], [2, '2″']].reduce((a, b) => Math.abs(b[0] - g) < Math.abs(a[0] - g) ? b : a)[1];
/* ════════════ easing ════════════ */
const seg = (t, a, b) => clamp((t - a) / (b - a));
const eio = p => p < .5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
const backOut = p => { const c1 = 1.25, c3 = c1 + 1; return 1 + c3 * Math.pow(p - 1, 3) + c1 * Math.pow(p - 1, 2); };
const bump = p => p < .62 ? Math.pow(p / .62, 1.6) : 1 - .1 * Math.sin(Math.PI * (p - .62) / .38) * (1 - (p - .62) / .38);

/* ════════════ timelines ════════════
   theta (door), turn (key, may overshoot), keyIn, pulse, ly (letter bottom, door-local), lop */
const fadeOut = p => 1 - Math.pow(Math.max(0, (p - .6) / .4), 2);
function tlFull(t, G) {
  const open = G.open, sel = parseFloat(selections.gap);
  // the latch setting: shown at ½″, then 1″, then 2″, then back to the chosen setting
  let gv = sel;
  if (t < 3.4) gv = .5;
  else if (t < 3.7) gv = .5 + .5 * eio(seg(t, 3.4, 3.7));
  else if (t < 3.95) gv = 1;
  else if (t < 4.25) gv = 1 + .5 * eio(seg(t, 3.95, 4.25));
  else if (t < 4.5) gv = 1.5;
  else if (t < 4.8) gv = 1.5 + .5 * eio(seg(t, 4.5, 4.8));
  else if (t < 4.9) gv = 2;
  else if (t < 5.2) gv = 2 + (sel - 2) * eio(seg(t, 4.9, 5.2));
  const lock = lockFor(G, gv);
  let theta = 0;
  if (t >= 2 && t < 2.9) theta = lock * bump(seg(t, 2, 2.9));
  else if (t >= 2.9 && t < 7.6) theta = lock;
  else if (t >= 7.6 && t < 8.2) theta = lock * (1 - eio(seg(t, 7.6, 8.2)));
  else if (t >= 11 && t < 12.1) theta = open * eio(seg(t, 11, 12.1));
  else if (t >= 12.1 && t < 13.4) theta = open;
  else if (t >= 13.4 && t < 14.4) theta = open * (1 - eio(seg(t, 13.4, 14.4)));
  // opens on the key locking the box; it ends unlocked with the key still in, so the loop is seamless
  const turn = t < 1.6 ? 1 - eio(seg(t, .5, 1.1)) : backOut(seg(t, 10.05, 10.7));
  const keyIn = t < 1.6 ? eio(seg(t, .05, .4)) - eio(seg(t, 1.2, 1.5)) : eio(seg(t, 9.6, 10)) - eio(seg(t, 14.45, 14.8));
  const pulse = t >= 1.05 && t < 1.6 ? seg(t, 1.05, 1.6) : t >= 10.6 && t < 11.3 ? seg(t, 10.6, 11.3) : -1;
  const chip = clamp(seg(t, 2.6, 2.9) - seg(t, 5.1, 5.4));
  let mp = 0, lift = 0, lop = 0;
  if (t >= 5 && t < 5.4) lop = seg(t, 5, 5.4);
  else if (t >= 5.4 && t < 7.6) { lop = 1; mp = .5 * seg(t, 5.4, 6.8); }
  else if (t >= 7.6 && t < 12.2) { lop = 1; mp = .5 + .5 * eio(seg(t, 7.6, 8.3)); }
  else if (t >= 12.2 && t < 13.4) { mp = 1; lift = seg(t, 12.2, 13.4); lop = fadeOut(lift); }
  // the measurement clears out just before the mail appears (site change: it used to fade while the mail crossed it)
  const dimOut = seg(t, 4.95, 5.05);
  return { theta, turn, keyIn, pulse, mp, lift, lop, gv, chip, dimOut };
}
tlFull.dur = 14.8;
tlFull.beats = [[0, 5], [5, 9.6], [9.6, 14.8]];

function tlCarrier(t, G) {
  const lock = lockAngle(G);
  let theta = 0;
  if (t >= .6 && t < 1.5) theta = lock * bump(seg(t, .6, 1.5));
  else if (t >= 1.5 && t < 3.6) theta = lock;
  else if (t >= 3.6 && t < 4.2) theta = lock * (1 - eio(seg(t, 3.6, 4.2)));
  let mp = 0, lop = 0;
  if (t >= 1.3 && t < 1.7) lop = seg(t, 1.3, 1.7);
  else if (t >= 1.7 && t < 3.6) { lop = 1; mp = .5 * seg(t, 1.7, 3.2); }
  else if (t >= 3.6) { mp = .5 + .5 * eio(seg(t, 3.6, 4.3)); lop = 1 - seg(t, 5.4, 5.9); }
  return { theta, turn: 0, keyIn: 0, pulse: -1, mp, lift: 0, lop };
}
tlCarrier.dur = 6;
tlCarrier.beats = [[0, 1.7], [1.7, 3.6], [3.6, 6]];

function tlOwner(t, G) {
  const open = G.open;
  const turn = t < 4.5 ? backOut(seg(t, .5, 1.1)) : 1 - eio(seg(t, 4.6, 5.1));
  const keyIn = eio(seg(t, .1, .45)) - eio(seg(t, 5.2, 5.6));
  const pulse = t >= 1.05 && t < 1.7 ? seg(t, 1.05, 1.7) : -1;
  let theta = 0;
  if (t >= 1.3 && t < 2.4) theta = open * eio(seg(t, 1.3, 2.4));
  else if (t >= 2.4 && t < 3.6) theta = open;
  else if (t >= 3.6 && t < 4.5) theta = open * (1 - eio(seg(t, 3.6, 4.5)));
  let mp = 1, lift = 0, lop = 1;
  if (t >= 2.5 && t < 3.6) { lift = seg(t, 2.5, 3.6); lop = fadeOut(lift); }
  else if (t >= 3.6 && t < 5.5) lop = 0;
  else if (t >= 5.5) lop = seg(t, 5.5, 5.9);
  return { theta, turn, keyIn, pulse, mp, lift, lop };
}
tlOwner.dur = 6;
tlOwner.beats = [[0, 1.3], [1.3, 2.5], [2.5, 3.6], [3.6, 6]];

/* ════════════ copy ════════════ */
const BEAT_COPY = {
  steps: g => [['01 · Locked', `Locked, the door still opens ${g}.`], ['02 · Mail in', 'Mail slides in and settles at the bottom.'], ['03 · Your key', 'Your key opens it all the way.']],
  roles: g => [['01 · Lock it up', `When locked, the door cracks open up to ${g}.`], ['02 · For your carrier', 'Just enough room for your carrier to deliver your mail and small packages.'], ['03 · For you', 'When unlocked, it opens wide.']],
  verbs: g => [['01 · Tip', `Tip the door. It stops at ${g}.`], ['02 · Drop', 'Mail drops in and stays put.'], ['03 · Turn', 'Turn the key to open it all the way.']],
  faq:   g => [['Is it locked?', `Yes. The door only tips open ${g}.`], ['How does mail get in?', 'Through that opening, down to the bottom.'], ['How do I get it out?', 'Your key opens the door all the way.']],
  quiet: g => [['01', 'Locked.'], ['02', 'Mail still gets in.'], ['03', 'Your key lets you in.']],
};
// the optional fourth beat: the lock doesn't have to be used
const OPT_COPY = {
  steps: ['04 · Your choice', 'Or leave it unlocked. It opens like any mailbox.'],
  roles: ['04 · Your call', 'And you can always leave it unlocked if you’d prefer.'],
  verbs: ['04 · Or don’t', 'Skip the key, and it opens freely.'],
  faq:   ['Do I have to lock it?', 'No. Leave it unlocked, and it opens like any mailbox.'],
  quiet: ['04', 'Or leave it unlocked.'],
};
const COPY = {
  full: () => [...BEAT_COPY[selections.copy](gapLabel()), ...(selections.optional === 'on' ? [OPT_COPY[selections.copy]] : [])].map(([k, t]) => ({ k, t })),
  carrier: () => [`The door tips open ${gapLabel()}, then stops.`, 'Mail slides in.', 'Still locked when they leave.'],
  owner: () => ['Turn the key.', 'The door opens all the way.', 'Mail in hand.', 'Lock it again.'],
};

/* ════════════ drawing ════════════ */
function viewBox(G) { const v = G.vb.split(' ').map(Number); return { x: v[0], y: v[1], w: v[2], h: v[3] }; }

function sceneMarkup(sc, st, time) {
  const G = GEOM[sc.product], F = FINISH[selections.finish], id = sc.id, clear = selections.clear / 100;
  const { H, L, C, S } = G;
  const lock = lockAngle(G);
  const phi = G.base + st.theta, cs = Math.cos(phi), sn = Math.sin(phi), dg = phi * 180 / Math.PI;
  const X = lx => n2(H.x + lx), Y = ly => n2(H.y + ly);
  const rot = `rotate(${n2(dg)} ${H.x} ${H.y})`;
  const camT = clamp(st.turn);

  // latch tab hook: placed so it meets the cam exactly at the lock angle
  const A = { x: C.x - 2.5, y: C.y + 8 }, a = -(G.base + lock), ddx = A.x - H.x, ddy = A.y - H.y;
  const hk = { x: ddx * Math.cos(a) - ddy * Math.sin(a), y: ddx * Math.sin(a) + ddy * Math.cos(a) };

  let s = `<defs>
    <linearGradient id="${id}-pg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${F.light}"/><stop offset=".55" stop-color="${F.main}"/><stop offset="1" stop-color="${F.dark}"/></linearGradient>
    <linearGradient id="${id}-wg" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#e3e3df"/><stop offset="1" stop-color="#fbfbf9"/></linearGradient>
    <radialGradient id="${id}-ch" cx=".35" cy=".35" r=".8"><stop offset="0" stop-color="#ffffff"/><stop offset=".55" stop-color="#c5c7c9"/><stop offset="1" stop-color="#8b8e91"/></radialGradient>
    <linearGradient id="${id}-brass" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#f0d688"/><stop offset=".55" stop-color="#cfa547"/><stop offset="1" stop-color="#9c7526"/></linearGradient>
    <pattern id="${id}-hatch" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="7" stroke="rgba(0,0,0,.13)" stroke-width="1"/></pattern>
  </defs>`;

  // environment
  if (G.scene === 'wall') {
    s += `<rect x="-100" y="-500" width="180" height="1500" fill="#e2ddd4"/><rect x="-100" y="-500" width="180" height="1500" fill="url(#${id}-hatch)"/><line x1="80" y1="-500" x2="80" y2="1000" stroke="rgba(0,0,0,.22)"/>`;
    s += `<rect x="80" y="362" width="118" height="5" fill="rgba(0,0,0,.06)"/>`;
  } else {
    s += `<g opacity="${n2(1 - clear * .92)}"><path d="M226,287 L258,287 L-41,1500 L-73,1500 Z" fill="${F.dark}"/><rect x="146" y="280" width="118" height="7" rx="1" fill="${F.dark}"/></g>`;
    if (clear > .05) s += `<g fill="none" stroke="${F.dark}" stroke-width="1" stroke-linejoin="round" opacity="${n2(clear * .45)}"><path d="M226,287 L258,287 L-41,1500 L-73,1500 Z"/><rect x="146" y="280" width="118" height="7" rx="1"/></g>`;
  }
  s += `<path d="${G.side}" fill="#d9d6cf" opacity="${n2(clear)}"/>`;

  // moving with the door, behind the shell: wing, envelope, intrusion flap, latch tab
  s += `<g transform="${rot}">`;
  s += `<polygon points="${X(-2)},${Y(-6)} ${X(-2)},${Y(-L + 6)} ${X(-G.wing)},${Y(-L + 16)} ${X(-12)},${Y(-8)}" fill="url(#${id}-wg)" stroke="#c9c8c2" stroke-width=".8"/>`;
  s += `</g>`;
  if (st.lop > 0) s += mailMarkup(G, mailPose(G, st, phi), st.lop, id);
  s += `<g transform="${rot}"><path d="M${X(-2)},${Y(-L + 2)} L${X(-15)},${Y(-L + 12)}" stroke="#d7d6d0" stroke-width="3.2" stroke-linecap="round"/>`;
  const tabX = hk.x - 3.5, tabW = -2 - tabX;
  s += `<rect x="${X(tabX)}" y="${Y(hk.y + 7)}" width="${n2(tabW)}" height="4" fill="#c9ccce" stroke="#5f6366" stroke-width=".7"/><rect x="${X(tabX)}" y="${Y(hk.y - 5)}" width="4" height="16" rx="1" fill="#d6d8da" stroke="#5f6366" stroke-width=".7"/>`;
  s += `<rect x="${X(tabX + tabW * .45)}" y="${Y(hk.y + 8)}" width="6" height="2" rx="1" fill="#5d6063"/><rect x="${X(tabX + tabW * .7)}" y="${Y(hk.y + 8)}" width="6" height="2" rx="1" fill="#5d6063"/></g>`;

  // lock cam — turns with the key
  s += `<g transform="rotate(${n2(camT * 90)} ${C.x} ${C.y})"><rect x="${C.x - 2.6}" y="${C.y - 2}" width="5.2" height="14" rx="2" fill="#3f4346"/></g><circle cx="${C.x}" cy="${C.y}" r="5.5" fill="#8e9295"/>`;

  // shell side
  s += `<g opacity="${n2(1 - clear * .92)}">`;
  s += G.hood ? `<path d="${G.side}" fill="#f7f7f4"/><path d="${G.hood}" fill="url(#${id}-pg)"/>` : `<path d="${G.side}" fill="url(#${id}-pg)"/>`;
  s += `</g>`;
  s += `<path d="${G.side}" fill="none" stroke="${F.dark}" stroke-width="1.1" stroke-dasharray="${clear > .45 ? '4 3' : 'none'}" opacity="${n2(.1 + clear * .6)}"/>`;

  // door slab + handle
  s += `<g transform="${rot}"><rect x="${X(-2.5)}" y="${Y(-L)}" width="5" height="${n2(L)}" rx="2" fill="#fafaf8" stroke="#c6c5bf" stroke-width=".8"/>`;
  const hy = -G.handle * L;
  s += `<g transform="rotate(-10 ${X(2)} ${Y(hy)})"><rect x="${X(1)}" y="${Y(hy - 3)}" width="22" height="5.5" rx="1.2" fill="${F.main}"/><rect x="${X(1)}" y="${Y(hy - 3)}" width="22" height="2" rx="1" fill="${F.light}" opacity=".7"/></g>`;
  s += `<circle cx="${X(0)}" cy="${Y(0)}" r="2.4" fill="#b4b3ad"/></g>`;

  // lock face, key, click
  s += keyMarkup(G, st, id, clear);

  // opening dimension
  // steps aside while the envelope passes through the opening
  const dimOp = (camT < .2 ? clamp(st.theta / lock) * (st.theta <= lock * 1.05 ? 1 : 0) : 0) * (st.mp >= 1 || st.lop <= 0 ? 1 : 1 - clamp(st.lop)) * (1 - (st.dimOut || 0));
  if (dimOp > .02 && !sc.clean) {
    const x0 = H.x + L * Math.sin(G.base), x1 = H.x + L * Math.sin(phi), y = G.dimY;
    s += `<g opacity="${n2(dimOp)}" stroke="#1d1d1d" stroke-width="1.1"><line x1="${n2(x0)}" y1="${y}" x2="${n2(x1)}" y2="${y}"/><line x1="${n2(x0)}" y1="${y - 5}" x2="${n2(x0)}" y2="${y + 5}"/><line x1="${n2(x1)}" y1="${y - 5}" x2="${n2(x1)}" y2="${y + 5}"/>
      <line x1="${n2(x0)}" y1="${y + 5}" x2="${n2(x0)}" y2="${n2(H.y - L * Math.cos(G.base) - 4)}" stroke-dasharray="2 2" opacity=".35"/></g>
      <text x="${n2(x1 + 8)}" y="${y + 5}" font-family="Josefin Sans" font-weight="700" font-size="15" fill="#1d1d1d" opacity="${n2(dimOp)}">${fmtGap(GAP_NOW ?? parseFloat(selections.gap))}</text>`;
  }
  if (st.chip > .01 && !sc.clean) {
    // the latch adjusts in three steps — the current one darkens as the door widens
    const g = GAP_NOW ?? parseFloat(selections.gap), vb = viewBox(G), chipW = 240;
    let x = n2(Math.min(H.x + L * Math.sin(G.base) - 6, vb.x + vb.w - chipW - 10)), y = G.dimY - 30;
    { // site change: the lab spot sits on the mail's path; use the open room the stage has, when it has it
      const V = sc.vis || { x: vb.x, w: vb.w }, top = H.x + L * Math.sin(G.base + lockFor(G, 2)), topY = H.y - L * Math.cos(G.base);
      if (top + 40 + chipW <= V.x + V.w - 12) { x = n2(top + 40); y = n2(topY + 70); }
      else if (top - 60 - chipW >= V.x + 12) x = n2(top - 60 - chipW);
    }
    const steps = [['½″', .5], ['1″', 1], ['1½″', 1.5], ['2″', 2]];
    s += `<g opacity="${n2(st.chip)}" font-family="Josefin Sans" font-weight="700"><text x="${x}" y="${y}" font-size="11" letter-spacing="1.8" fill="#1d5d63">ADJUSTS TO</text>`
      + steps.map(([lbl, v], i) => { const on = Math.abs(g - v) < .26; return `<g transform="translate(${x + 86 + i * 39} ${y - 13})"><rect width="35" height="19" rx="9.5" fill="${on ? '#1d1d1d' : 'none'}" stroke="#1d1d1d" stroke-width="1" opacity="${on ? 1 : .35}"/><text x="17.5" y="14" text-anchor="middle" font-size="12.5" fill="${on ? '#fff' : '#1d1d1d'}" opacity="${on ? 1 : .5}">${lbl}</text></g>`; }).join('') + `</g>`;
  }
  return s;
}

/* ════════════ key ════════════
   A key twists on its own axis — the bow spins in place, it never swings around the lock.
   Four ways to show that quarter turn. */
const KEY_ART = id => `<rect x="0" y="-2.3" width="11.5" height="4.6" fill="url(#${id}-brass)"/>
  <rect x="10" y="-5" width="4.5" height="10" rx="1.2" fill="url(#${id}-brass)"/>
  <path d="M14,0 C14,-9 20,-13 27,-13 C34,-13 39,-7.5 39,0 C39,7.5 34,13 27,13 C20,13 14,9 14,0 Z" fill="url(#${id}-brass)" stroke="#8f6c22" stroke-width=".7" vector-effect="non-scaling-stroke"/>
  <circle cx="33" cy="0" r="3.2" fill="#ece7df" stroke="#8f6c22" stroke-width=".6" vector-effect="non-scaling-stroke"/>
  <path d="M19,-7 C21,-10 26,-11 30,-10" fill="none" stroke="#fff3c9" stroke-width="1.3" opacity=".75" vector-effect="non-scaling-stroke"/>`;

// end-on: the bow seen edge-first, a slim bar centred on the keyway
const KEY_END = (id, k = 1) => `<rect x="${-2.6 * k}" y="${-15 * k}" width="${5.2 * k}" height="${30 * k}" rx="${2.6 * k}" fill="url(#${id}-brass)" stroke="#8f6c22" stroke-width=".7" vector-effect="non-scaling-stroke"/>
  <rect x="${-1 * k}" y="${-13 * k}" width="${1.3 * k}" height="${26 * k}" rx="${.6 * k}" fill="#fff3c9" opacity=".55"/>
  <circle r="${5.4 * k}" fill="url(#${id}-brass)" stroke="#8f6c22" stroke-width=".7" vector-effect="non-scaling-stroke"/>`;

function keyMarkup(G, st, id, clear) {
  const C = G.C, style = selections.key, a = st.turn * 90, kOp = clamp(st.keyIn);
  const big = style === 'keyway';
  let s = '';
  // cylinder face — the keyway slot always turns with the key
  s += `<circle cx="${C.x}" cy="${C.y}" r="${big ? 10 : 7.6}" fill="url(#${id}-ch)" stroke="#6f7376" stroke-width=".7" opacity="${n2(1 - clear * .45)}"/>`;
  s += `<g transform="rotate(${n2(a)} ${C.x} ${C.y})"><rect x="${C.x - 1}" y="${C.y - (big ? 6 : 4.6)}" width="2" height="${big ? 12 : 9.2}" rx="1" fill="#3a3c3e"/></g>`;

  if (kOp > 0 && style === 'twist') {
    // a plain, unskewed key pointing out of the lock; the quarter turn reads as the bow
    // narrowing to its edge (a coin-flip), never as the key swinging around the lock
    const r = deg(a), w = Math.max(.1, Math.abs(Math.cos(r))), off = (1 - kOp) * 18, ang = 128;
    const dx = Math.cos(deg(ang)) * off, dy = Math.sin(deg(ang)) * off;
    const tf = `translate(${n2(C.x + dx)} ${n2(C.y + dy)}) rotate(${ang}) scale(1.5 ${n2(1.5 * w)})`;
    const edge = 1 - w;   // as it turns edge-on, the brass edge darkens
    s += `<g opacity="${n2(kOp)}"><g transform="translate(2 2.5)" opacity=".16"><g transform="${tf}">${KEY_ART(id).replace(/url\(#[^)]+\)|#[0-9a-f]{6}/g, '#000')}</g></g>
      <g transform="${tf}">${KEY_ART(id)}<path d="M14,0 C14,-9 20,-13 27,-13 C34,-13 39,-7.5 39,0 C39,7.5 34,13 27,13 C20,13 14,9 14,0 Z" fill="#6b4f17" opacity="${n2(edge * .45)}"/></g></g>`;
  }
  if (kOp > 0 && style === 'faceon') {
    const sc = .7 + .3 * kOp;
    s += `<g opacity="${n2(kOp)}" transform="translate(${C.x} ${C.y}) rotate(${n2(a)}) scale(${n2(sc)})"><g transform="translate(2 2.5)" opacity=".16">${KEY_END(id, 1.45).replace(/url\(#[^)]+\)|#[0-9a-f]{6}/g, '#000')}</g>${KEY_END(id, 1.45)}</g>`;
  }
  if (kOp > 0 && style === 'inset') {
    const I = G.inset, P = 1.0;
    const dx = C.x - I.x, dy = C.y - I.y, d = Math.hypot(dx, dy);
    s += `<g opacity="${n2(kOp)}">
      <line x1="${n2(I.x + dx / d * I.r)}" y1="${n2(I.y + dy / d * I.r)}" x2="${n2(C.x - dx / d * 11)}" y2="${n2(C.y - dy / d * 11)}" stroke="#1d1d1d" stroke-width=".8" opacity=".5"/>
      <circle cx="${C.x}" cy="${C.y}" r="11" fill="none" stroke="#1d1d1d" stroke-width=".8" opacity=".5"/>
      <circle cx="${I.x}" cy="${I.y}" r="${I.r}" fill="#f6f4f0" stroke="#1d1d1d" stroke-width="1.1"/>
      <circle cx="${I.x}" cy="${I.y}" r="${I.r * .56}" fill="url(#${id}-ch)" stroke="#6f7376" stroke-width="1"/>
      <g transform="translate(${I.x} ${I.y}) rotate(${n2(a)})"><g transform="translate(2.5 3.5)" opacity=".16">${KEY_END(id, I.r / 30).replace(/url\(#[^)]+\)|#[0-9a-f]{6}/g, '#000')}</g>${KEY_END(id, I.r / 30)}</g>`;
    if (st.pulse >= 0) s += `<circle cx="${I.x}" cy="${I.y}" r="${n2(I.r * (.62 + .3 * st.pulse))}" fill="none" stroke="#ed760e" stroke-width="2" opacity="${n2(1 - st.pulse)}"/>`;
    s += `</g>`;
  }
  if (st.pulse >= 0 && style !== 'inset') {
    const p = st.pulse;
    s += `<circle cx="${C.x}" cy="${C.y}" r="${n2((big ? 12 : 10) + 22 * p)}" fill="none" stroke="#ed760e" stroke-width="${n2(2 - p * 1.4)}" opacity="${n2(1 - p)}"/>`;
  }
  return s;
}

/* ════════════ mail ════════════
   Real sizes, in inches (long side, short side, thickness). The face is drawn turned toward you
   (squeezed to 40% along the box's depth) so it reads as mail; height and thickness are true to scale.
   Courier mail stands upright (long side vertical); Aerobox mail goes in landscape and lies flat. */
const ITEMS = {
  classic:  { l: 9.5, s: 4.125, t: .1 },
  airmail:  { l: 9.5, s: 4.125, t: .1 },
  postcard: { l: 6, s: 4.25, t: .05 },
  kraft:    { l: 12, s: 9, t: .2 },
  stack:    { l: 9.5, s: 4.125, t: .45 },
  magazine: { l: 10.5, s: 8, t: .35 },
  bubble:   { l: 10, s: 6, t: .8 },
  box:      { l: 6, s: 4, t: 1.75 },
};
const FACE = .4, FACE_ANG = deg(20), FACE_DX = FACE * Math.cos(FACE_ANG);
// the Aerobox slot runs across the box, so its mail is seen nearly edge-on
const faceOf = G => G.scene === 'wall' ? FACE : .14;
function mailDims(G) {
  const it = ITEMS[selections.letter], up = true, S = G.S;
  const w = (up ? it.s : it.l) * S, h = (up ? it.l : it.s) * S, t = it.t * S;
  return { w, h, t, wp: w * FACE_DX * faceOf(G) / FACE };
}
function restOf(G, d) {
  if (G.scene === 'wall') {
    // stands on the floor leaning lightly back, wholly inside the walls (inner back 84, inner front 190)
    let psi = deg(-8), lo = 84 + d.wp + d.h * Math.sin(-psi), hi = 189 - d.t;
    if (lo > hi) { psi = 0; lo = 84 + d.wp; }
    return { x: lo > hi ? hi : (lo + hi) / 2, y: 357, psi };
  }
  // lies flat on the floor, top end toward the door, centred in the box
  return { x: 250 - d.h / 2, y: 277, psi: deg(90) };
}
// pose = top-near corner (x, y) + tilt psi. mp 0–.5: in through the opening; mp .5–1: settles mid-box
// (timed with the door closing). Exits use lift (0–1) or, for "ride", the door's own swing.
function mailPose(G, st, phi) {
  const d = mailDims(G), h = d.h, lx = -8 - d.t, L = G.L, lock = lockAngle(G);
  const onDoor = (v, ph = phi) => { const c = Math.cos(ph), s = Math.sin(ph); return { x: G.H.x + lx * c - v * s, y: G.H.y + lx * s + v * c, psi: ph }; };
  const fromB = (b, psi) => ({ x: b.x + h * Math.sin(psi), y: b.y - h * Math.cos(psi), psi });
  const bottomOf = q => ({ x: q.x - h * Math.sin(q.psi), y: q.y + h * Math.cos(q.psi) });
  const mixB = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
  const start = -L - 10 - h, inside = -L + 16, low = -6 - h;
  const R = restOf(G, d), restB = { x: R.x, y: R.y }, restPsi = R.psi, floor = R.y;
  const rest = fromB(restB, restPsi);
  const mode = selections.mailIn;
  let pose = rest;

  if (G.scene === 'yard' && st.mp < 1) {
    // the Aerobox is low: mail rides down the door, falls back like a ladder sliding down a wall
    // (top end stays against the door, so it never rises toward the lid), then slides back to centre
    const p = Math.min(1, st.mp / .5), q = st.mp > .5 ? (st.mp - .5) / .5 : 0;
    const a0 = onDoor(low), B0 = bottomOf(a0), xT = B0.x + h * Math.sin(a0.psi);
    // the top end slides down the front wall, never past it (inner front ≈ 424)
    const FRONT = 424, xEnd = Math.min(xT, FRONT);
    const ladder = k => { const a = a0.psi + (Math.PI / 2 - a0.psi) * k, xt = xT + (xEnd - xT) * k; return fromB({ x: xt - h * Math.sin(a), y: floor }, a); };
    const flatFront = { x: xEnd - h, y: floor };
    const slideBack = k => fromB({ x: flatFront.x + (restB.x - flatFront.x) * k, y: floor }, Math.PI / 2);
    if (mode === 'tumble') {
      // one unbroken motion while the door is still open: down the door gathering speed, lies back with its
      // top sliding down the inside of the door, then glides straight on to its resting spot
      if (st.mp >= .5) return rest;
      if (p < .4) return onDoor(start + (low - start) * Math.pow(p / .4, 2));
      const sTop = -low, ladderAt = k => {
        const T = onDoor(-sTop * (1 - k)), y = B0.y + (floor - B0.y) * k, dy = y - T.y;
        const bx = T.x - Math.sqrt(Math.max(0, h * h - dy * dy));
        return { x: T.x, y: T.y, psi: Math.atan2(T.x - bx, y - T.y), bx };
      };
      if (p < .7) { const L = ladderAt((p - .4) / .3); return { x: L.x, y: L.y, psi: L.psi }; }
      const e = 1 - Math.pow(1 - (p - .7) / .3, 2), b1 = ladderAt(1).bx;
      return fromB({ x: b1 + (restB.x - b1) * e, y: floor }, Math.PI / 2);
    }
    if (mode === 'drop') {
      const enter = .3;
      if (p < enter) return onDoor(start + (inside - start) * eio(p / enter));
      if (q === 0) { const R0 = onDoor(inside), b0 = bottomOf(R0), f = (p - enter) / (1 - enter); const g = f < .82 ? Math.pow(f / .82, 2) : 1 - .06 * Math.sin(Math.PI * (f - .82) / .18); return fromB({ x: b0.x, y: b0.y + (floor - b0.y) * g }, R0.psi); }
      return q < .6 ? ladder(eio(q / .6)) : slideBack(eio((q - .6) / .4));
    }
    if (st.mp <= .5) return onDoor(start + (low - start) * eio(p));
    return q < .6 ? ladder(eio(q / .6)) : slideBack(eio((q - .6) / .4));
  }

  if (st.mp < 1) {
    const p = Math.min(1, st.mp / .5), q = st.mp > .5 ? (st.mp - .5) / .5 : 0;
    if (mode === 'slide') {
      // rides the door all the way down, then slides back off it as the door closes
      if (st.mp <= .5) pose = onDoor(start + (low - start) * eio(p));
      else { const a = onDoor(low); pose = fromB(mixB(bottomOf(a), restB, eio(q)), a.psi + (restPsi - a.psi) * eio(q)); }
    } else if (mode === 'tumble') {
      // slides down the door gathering speed (no slow-down), then falls back into the box
      if (p < .6) pose = onDoor(start + (low - start) * Math.pow(p / .6, 2));
      else {
        const a = onDoor(low), f = (p - .6) / .4, g = f < .85 ? Math.pow(f / .85, 2) : 1 + .05 * Math.sin(Math.PI * (f - .85) / .15);
        pose = fromB(mixB(bottomOf(a), restB, Math.min(1, g)), a.psi + (restPsi - a.psi) * g);
      }
    } else {
      const enter = .3;
      if (p < enter) pose = onDoor(start + (inside - start) * eio(p / enter));
      else {
        const R0 = onDoor(inside), b0 = bottomOf(R0), f = (p - enter) / (1 - enter);
        if (mode === 'drop') {
          const g = f < .82 ? Math.pow(f / .82, 2) : 1 - .06 * Math.sin(Math.PI * (f - .82) / .18);
          const landed = { x: b0.x, y: b0.y + (floor - b0.y) * g };
          pose = q > 0 ? fromB(mixB({ x: b0.x, y: floor }, restB, eio(q)), R0.psi + (restPsi - R0.psi) * eio(q)) : fromB(landed, R0.psi);
        } else pose = rest;
      }
    }
    return pose;
  }

  const out = selections.mailOut, opening = st.theta > lock * 1.5;
  if (out === 'ride') {
    if (st.lift > 0) return onDoor(low - (L + h + 20) * eio(st.lift));
    if (opening) { const k = eio(clamp(st.theta / G.open)), a = onDoor(low); return fromB(mixB(restB, bottomOf(a), k), restPsi + (a.psi - restPsi) * k); }
    return rest;
  }
  if (st.lift > 0 && G.scene === 'yard' && out === 'lift') {
    // the way it came in, run backwards and smoothed: slides forward, its top end swings up just in front of
    // the lid's edge while the bottom drags along the floor, then it lifts off steeply and away
    const u = eio(st.lift), v = 1 - u;
    const P = [[restB.x + h, floor], [470, floor], [462, 120], [530, -150]];
    const T = [0, 1].map(i => v * v * v * P[0][i] + 3 * v * v * u * P[1][i] + 3 * v * u * u * P[2][i] + u * u * u * P[3][i]);
    const e = Math.asin(Math.min(Math.sin(deg(75)), Math.max(0, (floor - T[1]) / h)));
    return { x: T[0], y: T[1], psi: Math.PI / 2 - e, fade: 1 - .5 * clamp((u - .8) / .2) };
  }
  if (st.lift > 0) {
    if (out === 'rise') { const p = eio(st.lift); return { x: rest.x, y: rest.y - 70 * p, psi: restPsi, fade: 1 - st.lift }; }
    const p = eio(st.lift), u = 1 - p, O = G.out, P0 = [rest.x, rest.y];
    const pt = (o, i) => P0[i] + o[i];
    const bz = i => u * u * u * P0[i] + 3 * u * u * p * pt(O.d1, i) + 3 * u * p * p * pt(O.d2, i) + p * p * p * pt(O.d3, i);
    return { x: bz(0), y: bz(1), psi: restPsi + (O.psi - restPsi) * p };
  }
  return rest;
}

function mailMarkup(G, pose, lop, id) {
  const fc = faceOf(G), d = mailDims(G), eu = [-Math.cos(FACE_ANG) * fc, -Math.sin(FACE_ANG) * fc], ev = [-Math.sin(pose.psi), Math.cos(pose.psi)];
  const m = [eu[0], eu[1], ev[0], ev[1], pose.x, pose.y].map(v => Math.round(v * 1000) / 1000).join(' ');
  let s = '';
  // thickness: the edge facing the door, and the top edge — only worth drawing for parcels
  if (d.t > 4) {
    const sg = pose.psi > deg(45) ? -1 : 1, dn = [sg * Math.cos(pose.psi) * d.t, sg * Math.sin(pose.psi) * d.t], o = [pose.x, pose.y];
    const b = [o[0] + ev[0] * d.h, o[1] + ev[1] * d.h], tr = [o[0] + eu[0] * d.w, o[1] + eu[1] * d.w];
    const c = ITEM_EDGE[selections.letter] || ['#cfc8bb', '#e4ded2'];
    s += `<polygon points="${[o, b, [b[0] + dn[0], b[1] + dn[1]], [o[0] + dn[0], o[1] + dn[1]]].map(q => n2(q[0]) + ',' + n2(q[1])).join(' ')}" fill="${c[0]}" stroke="rgba(0,0,0,.25)" stroke-width=".6"/>`;
    s += `<polygon points="${[o, tr, [tr[0] + dn[0], tr[1] + dn[1]], [o[0] + dn[0], o[1] + dn[1]]].map(q => n2(q[0]) + ',' + n2(q[1])).join(' ')}" fill="${c[1]}" stroke="rgba(0,0,0,.25)" stroke-width=".6"/>`;
  }
  s += `<g transform="matrix(${m})">${drawItem(selections.letter, n2(d.w), n2(d.h), id)}</g>`;
  return `<g opacity="${n2(lop * (pose.fade ?? 1))}">${s}</g>`;
}
const ITEM_EDGE = { box: ['#a77a45', '#d9ae73'], bubble: ['#d9b55a', '#f0d58c'], magazine: ['#f4f1ea', '#e6e0d4'], stack: ['#e9e2d4', '#f4efe5'] };

// draws an item in its natural orientation, turned a quarter if the slot it travels in wants the other one
function drawItem(style, W, H, id) {
  const portraitNatural = style === 'magazine' || style === 'bubble';
  const portraitSlot = H > W;
  const fn = (w, h) => {
    if (style === 'magazine') return magazine(w, h, id);
    if (style === 'bubble') return bubbleBundle(w, h, id);
    if (style === 'box') return parcel(w, h);
    if (style === 'stack') return `<g transform="translate(14 -16)">${piece('kraft', w, h, id)}</g><g transform="translate(7 -8)">${piece('classic', w, h, id)}</g>${piece('airmail', w, h, id)}`;
    return piece(style, w, h, id);
  };
  if (portraitNatural === portraitSlot) return fn(W, H);
  return `<g transform="translate(${W} 0) rotate(90)">${fn(H, W)}</g>`;
}

const SW = 'vector-effect="non-scaling-stroke"';
function magazine(W, H, id) {
  // portrait: masthead across the top, a cover print, and an airmail letter lying across the front
  const ew = Math.min(W * .9, 6.5 * 22 * .9), eh = ew * .55;
  return `<rect width="${W}" height="${H}" rx="2" fill="#efe6d6" stroke="#8f8676" stroke-width=".9" ${SW}/>
    <rect x="${n2(W * .06)}" y="${n2(H * .04)}" width="${n2(W * .88)}" height="${n2(H * .1)}" fill="#1d1d1d"/>
    <rect x="${n2(W * .1)}" y="${n2(H * .075)}" width="${n2(W * .5)}" height="${n2(H * .035)}" rx="2" fill="#fff"/>
    <rect x="${n2(W * .06)}" y="${n2(H * .17)}" width="${n2(W * .88)}" height="${n2(H * .5)}" fill="#3e888f"/>
    <circle cx="${n2(W * .62)}" cy="${n2(H * .36)}" r="${n2(W * .2)}" fill="#ed760e"/>
    <rect x="${n2(W * .06)}" y="${n2(H * .52)}" width="${n2(W * .88)}" height="${n2(H * .15)}" fill="#1d5d63"/>
    <g transform="translate(${n2((W - ew) / 2 - W * .04)} ${n2(H - eh - H * .05)}) rotate(-4)">${piece('airmail', n2(ew), n2(eh), id)}</g>`;
}
function bubble(W, H, id) {
  // portrait padded mailer: soft corners, bubble texture, printed label, sealed flap at the top
  return `<defs><pattern id="${id}-bub" width="9" height="9" patternUnits="userSpaceOnUse"><circle cx="4.5" cy="4.5" r="2.6" fill="none" stroke="#c9a54c" stroke-width=".7" opacity=".55"/></pattern></defs>
    <rect width="${W}" height="${H}" rx="9" fill="#ecc96f" stroke="#b08a34" stroke-width=".9" ${SW}/>
    <rect width="${W}" height="${H}" rx="9" fill="url(#${id}-bub)"/>
    <path d="M6,${n2(H * .1)} H${n2(W - 6)}" stroke="#b08a34" stroke-width=".9" stroke-dasharray="3 2" ${SW}/>
    <rect x="${n2(W * .14)}" y="${n2(H * .38)}" width="${n2(W * .72)}" height="${n2(H * .3)}" rx="2" fill="#fffcf5"/>
    <rect x="${n2(W * .2)}" y="${n2(H * .44)}" width="${n2(W * .5)}" height="4" rx="2" fill="#4b463f"/><rect x="${n2(W * .2)}" y="${n2(H * .44 + 9)}" width="${n2(W * .4)}" height="4" rx="2" fill="#4b463f"/><rect x="${n2(W * .2)}" y="${n2(H * .44 + 18)}" width="${n2(W * .3)}" height="4" rx="2" fill="#4b463f"/>
    <rect x="${n2(W * .14)}" y="${n2(H * .72)}" width="${n2(W * .72)}" height="${n2(H * .08)}" fill="#fffcf5"/>${[...Array(14)].map((_, i) => `<rect x="${n2(W * .17 + i * W * .047)}" y="${n2(H * .735)}" width="${i % 3 ? 1.6 : 3}" height="${n2(H * .05)}" fill="#1d1d1d"/>`).join('')}`;
}
function bubbleBundle(W, H, id) {
  // portrait: the 10×6″ mailer at the back, then a #10 envelope, a 7×5″ card and a postcard, bottoms aligned
  const S = W / 6, letters = [['classic', 9.5, 4.125, -6], ['airmail', 7, 5, -13], ['postcard', 6, 4.25, -20]];
  return bubble(W, H, id) + letters.map(([st, l, sh, x]) => {
    const lw = sh * S, lh = l * S;
    return `<g transform="translate(${n2(x + W - lw - 4)} ${n2(H - lh)}) translate(${n2(lw)} 0) rotate(90)">${piece(st, n2(lh), n2(lw), id)}</g>`;
  }).join('');
}
function parcel(W, H) {
  // landscape small box: kraft board, a tape band, a shipping label
  return `<rect width="${W}" height="${H}" rx="1.5" fill="#c99a5e" stroke="#8a6232" stroke-width=".9" ${SW}/>
    <rect x="0" y="${n2(H * .42)}" width="${W}" height="${n2(H * .16)}" fill="#e2c08b" opacity=".85"/>
    <rect x="${n2(W * .1)}" y="${n2(H * .12)}" width="${n2(W * .42)}" height="${n2(H * .24)}" rx="1.5" fill="#fffcf5"/>
    <rect x="${n2(W * .14)}" y="${n2(H * .17)}" width="${n2(W * .3)}" height="3.5" rx="1.7" fill="#4b463f"/><rect x="${n2(W * .14)}" y="${n2(H * .17 + 7)}" width="${n2(W * .22)}" height="3.5" rx="1.7" fill="#4b463f"/>
    <rect x="${n2(W * .62)}" y="${n2(H * .66)}" width="${n2(W * .28)}" height="${n2(H * .2)}" fill="#fffcf5"/>${[...Array(8)].map((_, i) => `<rect x="${n2(W * .645 + i * W * .03)}" y="${n2(H * .69)}" width="${i % 2 ? 1.4 : 2.6}" height="${n2(H * .14)}" fill="#1d1d1d"/>`).join('')}`;
}

function piece(style, EW, EH, id) {
  EW = +EW; EH = +EH;
  const sw = 'vector-effect="non-scaling-stroke"';
  // stamp with perforated edge; local x runs right-to-left on screen, so x≈6 is the visible top-right corner
  const stamp = (fill = '#ed760e') => `<rect x="6" y="6" width="16" height="20" fill="${fill}" stroke="#fffdf8" stroke-width="1.8" stroke-dasharray="1.6 1.3" ${sw}/><rect x="8.6" y="8.6" width="10.8" height="14.8" fill="none" stroke="#fff3e3" stroke-width=".9" ${sw}/>`;
  const lines = (x, y, w, c = '#4b463f') => `<rect x="${n2(x)}" y="${n2(y)}" width="${n2(w)}" height="3.2" rx="1.6" fill="${c}"/><rect x="${n2(x)}" y="${n2(y + 7.5)}" width="${n2(w * .8)}" height="3.2" rx="1.6" fill="${c}"/><rect x="${n2(x)}" y="${n2(y + 15)}" width="${n2(w * .58)}" height="3.2" rx="1.6" fill="${c}"/>`;
  switch (style) {
    case 'airmail':
      return `<defs><pattern id="${id}-air" width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="12" height="12" fill="#ffffff"/><rect width="4" height="12" fill="#c8453c"/><rect x="6" width="4" height="12" fill="#2f5b97"/></pattern></defs>
        <rect width="${EW}" height="${EH}" rx="2.5" fill="url(#${id}-air)" stroke="#a89d88" stroke-width=".9" ${sw}/>
        <rect x="4.5" y="4.5" width="${n2(EW - 9)}" height="${n2(EH - 9)}" rx="1" fill="#fffefb"/>
        ${stamp('#2f5b97')}${lines(EW * .2, EH * .5, EW * .52)}`;
    case 'postcard':
      return `<defs><linearGradient id="${id}-sky" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fbe9cf"/><stop offset="1" stop-color="#f6d2a8"/></linearGradient></defs>
        <rect width="${EW}" height="${EH}" rx="2" fill="#fffdf8" stroke="#a89d88" stroke-width=".9" ${sw}/>
        <rect x="3.5" y="3.5" width="${n2(EW - 7)}" height="${n2(EH - 7)}" fill="url(#${id}-sky)"/>
        <circle cx="${n2(EW * .62)}" cy="${n2(EH * .4)}" r="${n2(EW * .15)}" fill="#ed760e"/>
        <path d="M3.5,${n2(EH * .7)} C${n2(EW * .3)},${n2(EH * .5)} ${n2(EW * .6)},${n2(EH * .62)} ${n2(EW - 3.5)},${n2(EH * .55)} L${n2(EW - 3.5)},${n2(EH - 3.5)} L3.5,${n2(EH - 3.5)} Z" fill="#3e888f"/>
        <path d="M3.5,${n2(EH * .82)} C${n2(EW * .4)},${n2(EH * .7)} ${n2(EW * .7)},${n2(EH * .86)} ${n2(EW - 3.5)},${n2(EH * .78)} L${n2(EW - 3.5)},${n2(EH - 3.5)} L3.5,${n2(EH - 3.5)} Z" fill="#1d5d63"/>`;
    case 'kraft':
      return `<rect width="${EW}" height="${EH}" rx="2.5" fill="#d8b47e" stroke="#9c7a4c" stroke-width=".9" ${sw}/>
        <rect x="${n2(EW * .12)}" y="${n2(EH * .44)}" width="${n2(EW * .76)}" height="${n2(EH * .38)}" rx="1.5" fill="#fffcf5"/>
        ${stamp()}${lines(EW * .2, EH * .5, EW * .56)}`;
    case 'plain':
      return `<rect width="${EW}" height="${EH}" rx="2.5" fill="#ffffff" stroke="#a89d88" stroke-width=".9" ${sw}/>${stamp('#3e888f')}${lines(EW * .2, EH * .52, EW * .5)}`;
    default: // classic
      return `<rect width="${EW}" height="${EH}" rx="2.5" fill="#fffcf5" stroke="#a89d88" stroke-width=".9" ${sw}/>
        ${stamp()}
        <path d="M17,11 q4,-2.4 8,0 t8,0 t8,0 M17,16 q4,-2.4 8,0 t8,0 t8,0 M17,21 q4,-2.4 8,0 t8,0 t8,0" fill="none" stroke="#6f685d" stroke-width=".8" opacity=".55" ${sw}/>
        ${lines(EW * .2, EH * .52, EW * .55)}`;
  }
}

  /* ════════════════════════ instances ════════════════════════ */
  // the beats as single frames (door at the latch · mail inside · door open · unlocked, key out)
  const STILLS = [4.95, 7.55, 12.15, 16.5];
  // the beats as stretches of the story, for pages that play one at a time
  const RANGES = [[0, 5.2], [5, 9.6], [9.6, 14.8], [14.8, 17]];
  // Three beats: pause on each illustration for `hold` seconds (door at the latch · mail inside · door open)
  const HOLDS = [4.95, 7.55, 12.15];
  function holdRemap(t, h) { for (const p of HOLDS) { if (t < p) return t; if (t < p + h) return p; t -= h; } return t; }
  const BASE = { beats: [...tlFull.beats, [tlFull.dur, 99]] };

  const AEROS = {};
  const aeroOf = v => AEROS[v] || (AEROS[v] = aeroGeom(v));
  const RM = window.matchMedia ? matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  const LOCK_ICON = '<svg viewBox="0 0 16 18" aria-hidden="true" focusable="false"><path data-shackle d="M4.5,8.5 V5.5 A3.5,3.5 0 0 1 11.5,5.5 V8.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><rect x="1.5" y="8" width="13" height="9.5" rx="2" fill="currentColor"/></svg>';
  const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

  let scenes = [], seq = 0, running = false;

  function beatsOf(sc) { selections = sc.sel; return COPY.full(); }

  function build(sc) {
    const B = beatsOf(sc), yard = sc.product === 'aerobox';
    sc.el.classList.add('mbl');
    sc.el.innerHTML = `<div class="mbl-stage${yard ? ' mbl-yard' : ''}">`
      + (sc.badge ? `<div class="mbl-badge" data-lock>${LOCK_ICON}<span data-locktext>Locked</span></div>` : '')
      + (sc.text ? `<div class="mbl-text"><div class="mbl-k" data-k>${esc(B[0].k)}</div><div class="mbl-t" data-t>${esc(B[0].t)}</div>`
        + `<div class="mbl-seg">${B.map((b, i) => `<button type="button" data-seg="${i}" aria-label="${esc(b.k)}"><b></b></button>`).join('')}</div></div>` : '')
      + `<svg class="mbl-scene" aria-hidden="true" focusable="false"></svg>`
      + `<p class="mbl-sr">${B.map(b => esc(b.k.replace(/^\d+ · /, '') + ': ' + b.t)).join(' ')}</p></div>`;
    sc.stage = sc.el.firstElementChild;
    sc.svg = sc.el.querySelector('svg.mbl-scene');
    const vb = viewBox(sc.product === 'courier' ? GEOM.courier : aeroOf(sc.sel.aero));
    // `vb` reframes the drawing (thumbnails); `clean` drops the measurement and the "Adjusts to" pills
    sc.svg.setAttribute('viewBox', sc.vb || `${n2(vb.x)} ${n2(vb.y)} ${n2(vb.w)} ${n2(vb.h)}`);
    sc.svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    sc.el.setAttribute('role', 'img');
    sc.el.setAttribute('aria-label', 'How the lock works. ' + B.map(b => b.t).join(' '));
    sc.lastBeat = -1; sc.dirty = true; sc.vis = null;
    requestAnimationFrame(() => measure(sc));
    sc.el.querySelectorAll('[data-seg]').forEach(b => b.addEventListener('click', e => { e.stopPropagation(); goBeat(sc, +b.dataset.seg); }));
  }

  // jump to a beat: a still frame with reduced motion, otherwise the loop picks up at that beat
  function goBeat(sc, i) {
    if (sc.reduced || sc.still != null) { sc.still = STILLS[Math.min(i, STILLS.length - 1)]; sc.dirty = true; return; }
    if (sc.range) return;
    const h = sc.sel.hold, start = (tlFull.beats[i] || [tlFull.dur])[0];
    sc.t0 = performance.now() - (start + h * HOLDS.filter(x => x < start).length) * 1000;
    sc.lastBeat = -1;
  }

  function mount(el, opts) {
    if (el.mbLock) return el.mbLock;
    opts = opts || {};
    const sc = {
      id: 'mbl' + (++seq), el, product: opts.product === 'courier' ? 'courier' : 'aerobox',
      sel: Object.assign({}, DEFAULTS, opts.set || {}), text: opts.text !== false, badge: opts.badge !== false, clean: !!opts.clean, vb: opts.vb || null,
      still: opts.t != null ? +opts.t : null, range: opts.range || null, t0: performance.now(),
      reduced: RM.matches, visible: false, wasActive: false, slide: el.closest('[data-media-slide]'),
    };
    // reduced motion: start on the first still unless the page asked for a specific frame
    if (sc.reduced && sc.still == null) sc.still = sc.range ? STILLS[Math.max(0, RANGES.findIndex(r => r[0] === sc.range[0]))] : STILLS[0];
    build(sc);
    // with reduced motion, a tap on a captioned drawing steps to the next still
    if (sc.reduced && sc.text) el.addEventListener('click', () => {
      const i = STILLS.indexOf(sc.still); sc.still = STILLS[(i + 1) % (beatsOf(sc).length)]; sc.dirty = true;
    });
    if ('ResizeObserver' in window) new ResizeObserver(() => { measure(sc); sc.dirty = true; }).observe(el);
    if ('IntersectionObserver' in window) new IntersectionObserver(es => es.forEach(e => { sc.visible = e.isIntersecting; }), { rootMargin: '80px' }).observe(el);
    else sc.visible = true;

    const api = {
      set(o) {
        Object.assign(sc.sel, o);
        if (o.product) sc.product = o.product === 'courier' ? 'courier' : 'aerobox';
        build(sc);
      },
      freeze(t) { sc.still = t == null ? (sc.reduced ? STILLS[0] : null) : +t; if (t == null) sc.t0 = performance.now(); sc.dirty = true; },
      range(r) { if (sc.reduced) return; sc.range = r; sc.still = null; sc.t0 = performance.now(); sc.lastBeat = -1; },
      step(i) { if (sc.reduced) { sc.range = null; this.freeze(STILLS[i]); } else this.range([...RANGES[i], true]); },
      // the lab's embed messages, kept as-is
      post(d) { d = d || {}; if (d.set) this.set(d.set); if ('freeze' in d) this.freeze(d.freeze); if (d.range) this.range(d.range); },
      get reducedMotion() { return sc.reduced; },
      get duration() { return sc.range ? sc.range[1] - sc.range[0] : null; },
    };
    el.mbLock = api;
    scenes.push(sc);
    if (!running) { running = true; requestAnimationFrame(frame); }
    return api;
  }

  // the visible slice of the drawing's coordinate space
  function measure(sc) {
    const w = sc.svg.clientWidth, h = sc.svg.clientHeight;
    if (!w || !h) return;
    const vb = viewBox(sc.product === 'courier' ? GEOM.courier : aeroOf(sc.sel.aero)), k = Math.min(w / vb.w, h / vb.h);
    sc.vis = { x: vb.x + vb.w / 2 - w / k / 2, w: w / k };
  }

  // a drawing in a product gallery only runs while its slide is the one showing
  function isActive(sc) {
    if (!sc.visible || document.hidden || !sc.el.isConnected) return false;
    const s = sc.slide;
    if (s && s.closest('.flickity-enabled') && !s.classList.contains('is-selected')) return false;
    return sc.el.offsetWidth > 0;
  }

  function frame(now) {
    for (const sc of scenes) {
      const on = isActive(sc);
      // arriving at a looping drawing starts it from the top
      if (on && !sc.wasActive && sc.still == null && !sc.range) { sc.t0 = now; sc.lastBeat = -1; }
      sc.wasActive = on;
      if (!on) continue;
      if (sc.still != null && !sc.dirty) continue;   // a held frame only needs drawing once
      sc.dirty = false;
      selections = sc.sel;
      GEOM.aerobox = aeroOf(sc.sel.aero);
      const G = GEOM[sc.product], t = Math.max(0, (now - sc.t0) / 1000);
      let st, tl, lt;
      if (sc.still != null) { lt = sc.still; st = tlFull(lt, G); tl = BASE; }
      else if (sc.range) {
        const [a, b, once] = sc.range;
        lt = once ? Math.min(b, a + t) : a + (t % (b - a));
        st = tlFull(lt, G); tl = BASE;
      } else {
        const h = sc.sel.hold, opt = sc.sel.optional === 'on' ? 4.8 + h : 0, dur = tlFull.dur + opt + HOLDS.length * h;
        lt = t % dur;
        st = tlFull(holdRemap(lt, h), G);
        tl = { beats: [...tlFull.beats, ...(opt ? [[tlFull.dur, tlFull.dur + opt]] : [])].map(w => w.map(b => b + h * HOLDS.filter(x => x < b).length)) };
      }
      const beat = Math.max(0, tl.beats.findIndex(w => lt >= w[0] && lt < w[1]));
      { // lock badge — the shackle lifts and swings as the key turns
        const c = clamp(st.turn), b = sc.stage.querySelector('[data-lock]');
        if (b) { b.classList.toggle('open', c > .5); b.querySelector('[data-shackle]').setAttribute('transform', `translate(0 ${n2(-2.6 * c)}) rotate(${n2(-28 * c)} 11.5 8.5)`);
          const tx = c > .5 ? 'Unlocked' : 'Locked', te = b.querySelector('[data-locktext]'); if (te.textContent !== tx) te.textContent = tx; }
      }
      GAP_NOW = st.gv ?? null;
      sc.svg.innerHTML = sceneMarkup(sc, st, t);
      GAP_NOW = null;
      syncText(sc, tl, lt, beat);
    }
    requestAnimationFrame(frame);
  }

  function syncText(sc, tl, lt, beat) {
    if (!sc.text) return;
    const B = COPY.full(), i = Math.min(beat, B.length - 1);
    if (sc.lastBeat !== i) {
      sc.lastBeat = i;
      sc.stage.querySelector('[data-k]').textContent = B[i].k;
      sc.stage.querySelector('[data-t]').textContent = B[i].t;
    }
    sc.stage.querySelectorAll('.mbl-seg b').forEach((b, j) => {
      const w = tl.beats[j] || [0, 1];
      const pct = sc.still != null ? (j <= i ? 100 : 0) : (j < i ? 100 : j > i ? 0 : clamp((lt - w[0]) / (w[1] - w[0])) * 100);
      b.style.width = pct.toFixed(1) + '%';
    });
  }

  function mountAll(root) {
    (root || document).querySelectorAll('[data-mb-lock]').forEach(el => {
      let o = {}; try { o = JSON.parse(el.getAttribute('data-mb-lock') || '{}'); } catch (e) {}
      mount(el, o);
    });
  }

  window.MBLock = { mount, mountAll, STILLS, RANGES, DEFAULTS, get reducedMotion() { return RM.matches; } };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountAll());
  else mountAll();
  document.dispatchEvent(new CustomEvent('mblock:ready'));
})();
